
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src2984367493/src/vl-app/pages/policy.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { Button, Modal, Space } from 'antd';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { useQueryClient } from '@tanstack/react-query';
import usePolicies from '@hooks/usePolicies';
import useSetTourAttributes from '@hooks/useSetTourAttributes';
import useUser from '@vl-core/hooks/useUser';
import { css } from '@emotion/react';
import { useDeepLink } from '@hooks/useDeepLink';
import { TransferMessageBody, useAutoSSOWithTransferModal } from '@hooks/useAutoSSO';
import Header from '@components/Layouts/Header/Header';

const Policy = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    isLoading: isPolicyLoading,
    refetch: refetchPolicies,
    policies,
    policiesAccepted: allPoliciesAccepted
  } = usePolicies();
  const user = useUser();
  const [acceptingPolicies, setAcceptingPolicies] = useState(false);
  const setTourAttributes = useSetTourAttributes();

  const { /*deepLinkUrl, */ pushToApp, pushToLogin } = useDeepLink();

  useAutoSSOWithTransferModal(
    <TransferMessageBody>
      <p>{t('To enable you to book an appointment, please read HBSUK’s terms and conditions.')}</p>
    </TransferMessageBody>
  );

  const redirectAfterPoliciesAccepted = async () => {
    const { self_reg_required, user_type_code } = user;

    await setTourAttributes(true);
    if (user_type_code === 'CLINICIAN') {
      return router.push('/clinician');
    }

    return self_reg_required ? router.push('/create-account') : pushToApp('/patients?afterLogin=true');
  };

  const focusRef = useCallback((node: HTMLButtonElement | null) => {
    if (node) node.focus();
  }, []);

  const confirmAllPolicies = () => {
    const { user_guid } = user;

    setAcceptingPolicies(true);

    Promise.all(
      policies
        ?.filter(({ accepted }) => !accepted)
        .map(({ policy_code }) =>
          dispatch(actions.acceptUserPolicy({ user_guid, policy_code, accepted: true }), false)
        ) || []
    )
      .then(async (policiesRes) => {
        policiesRes.forEach((policy) => {
          // if any of the policy accept calls fails, throw error to enter catch block
          if (policy.error) throw new Error();
        });
        sessionStorage.setItem('firstTimeLoginAfterAcceptingPolicy', '1');
        await redirectAfterPoliciesAccepted();
      })
      .catch(() => {
        setAcceptingPolicies(false);

        Modal.error({
          title: 'Failed to accept policies',
          content: 'There was an issue accepting one or more policies. Please try again.',
          okButtonProps: { 'data-testid': 'ok' },
          closable: true
        });
      })
      .finally(async () => {
        await refetchPolicies();
      });
  };

  const userLogout = async () => {
    const res = await dispatch(actions.userLogout(queryClient));
    if (res.type === actionTypes.AUTH_LOGOUT_SUCCESS) {
      await pushToLogin();
    }
  };

  const generateHTMLLink = (policy_code: string, desc: string) =>
    `I accept the <a rel="noreferrer" href='/patients/legal/?all_policies=0&activeTab=${policy_code}' tabIndex={0} role="link" target="_blank">${desc}</a>`;

  return (
    <>
      <Header pageType="login" pageTitle="Policy Review" forceShowLogo />

      <section
        className="section-layout policy-layout"
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 50vh;
          padding: 4em 0;

          h1 {
            font-weight: bold;
            font-size: 30px;
            color: var(--productGray);
            margin-bottom: 20px;

            > span {
              color: var(--productPrimary);
            }
          }
        `}
      >
        <h1>
          <span>{t('Policy')}</span> {t('Review')}
        </h1>

        <div className="policy-content">
          <div style={{ marginBottom: '8px' }}>
            {user?.new_policies
              ? t('To continue using the HBSUK platform, please accept the policies below.')
              : t('Please accept the updated policies below.')}
          </div>
          {!allPoliciesAccepted &&
            policies
              ?.filter(({ accepted }) => !accepted)
              ?.map((p) => (
                <div key={p.policy_code}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: p.policy_link || generateHTMLLink(p.policy_code, p.description)
                    }}
                  />
                  {'. '}
                </div>
              ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!isPolicyLoading && !acceptingPolicies) {
              confirmAllPolicies();
            }
          }}
        >
          <Space style={{ padding: '2em 0' }}>
            <Button htmlType="button" onClick={userLogout} data-testid="logout">
              {t('Logout')}
            </Button>
            <Button
              ref={focusRef}
              htmlType="submit"
              type="primary"
              loading={acceptingPolicies}
              disabled={isPolicyLoading}
              data-testid="accept"
            >
              {t('Accept')}
            </Button>
          </Space>
        </form>
      </section>
    </>
  );
};

const WrappedPolicy = () => {
  const { t } = useTranslation('common');
  const initialised = useInitialisedStore();

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return <Policy />;
};

export default WrappedPolicy;
export const Route = createFileRoute('/policy')({ component: WrappedPolicy });
